import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { initWebVitals, logQueryMetrics } from '@/utils/performance'
import App from './App'
import './index.css'

// Import cleanup helper (will auto-initialize in production)
import '@/utils/cleanup-helper'

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (import.meta.env.DEV) {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    console.error('[Unhandled Promise Rejection]', error)
  }
})

// Initialize Web Vitals tracking
initWebVitals()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Differentiated stale time based on data type
      staleTime: 1000 * 60 * 5, // 5 minutes (default)
      gcTime: 1000 * 60 * 10, // 10 minutes (React Query v5)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Track query performance metrics
const queryStartTimes = new Map<string, number>()

queryClient.getQueryCache().subscribe((event) => {
  const queryKey = JSON.stringify(event.query.queryKey)

  if (event.type === 'updated') {
    const state = event.query.state

    // Track fetch start
    if (state.fetchStatus === 'fetching' && !queryStartTimes.has(queryKey)) {
      queryStartTimes.set(queryKey, performance.now())
    }

    // Track fetch completion
    if (state.fetchStatus === 'idle' && queryStartTimes.has(queryKey)) {
      const startTime = queryStartTimes.get(queryKey)!
      const duration = performance.now() - startTime
      queryStartTimes.delete(queryKey)

      logQueryMetrics({
        queryKey,
        duration,
        status: state.status === 'error' ? 'error' : 'success',
        dataSize: state.data ? JSON.stringify(state.data).length : undefined,
        timestamp: Date.now(),
      })
    }
  }
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
)
